import React from 'react';

// Anomaly type configurations
const ANOMALY_TYPE_CONFIG = {
  temporal: {
    icon: '\u23F0', // clock
    label: 'Temporal',
    description: 'At known location but wrong time',
    color: '#8B5CF6', // purple
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
  },
  spatial: {
    icon: '\uD83D\uDCCD', // pin
    label: 'Spatial',
    description: 'At unexpected location',
    color: '#3B82F6', // blue
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
  },
  route: {
    icon: '\u27A1\uFE0F', // arrow
    label: 'Route',
    description: 'Unusual travel path',
    color: '#F59E0B', // amber
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
  },
  behavioral: {
    icon: '\u26A0\uFE0F', // warning
    label: 'Behavioral',
    description: 'Unusual movement pattern',
    color: '#EF4444', // red
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
  },
};

// Alert level configurations - aligned with spec thresholds
const ALERT_LEVEL_CONFIG = {
  none: {
    color: '#22C55E',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    borderColor: 'border-green-300',
    label: 'Normal',
    solid: false,
  },
  low: {
    color: '#F59E0B',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-300',
    label: 'Low Alert',
    solid: false,
  },
  medium: {
    color: '#F97316',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800',
    borderColor: 'border-orange-300',
    label: 'Medium Alert',
    solid: false,
  },
  high: {
    color: '#EF4444',
    bgColor: 'bg-red-600',
    textColor: 'text-white',
    borderColor: 'border-red-600',
    label: 'High Alert',
    solid: true, // Solid red for high alerts per spec
  },
};

/**
 * Confidence meter visualization
 */
const ConfidenceMeter = ({ confidence }) => {
  const percentage = Math.round(confidence * 100);
  const bars = 5;
  const filledBars = Math.round(confidence * bars);

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-gray-500 mr-1">Confidence:</span>
      <div className="flex gap-0.5">
        {[...Array(bars)].map((_, i) => (
          <div
            key={i}
            className={`w-2 h-3 rounded-sm ${
              i < filledBars ? 'bg-blue-500' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-gray-600 ml-1">{percentage}%</span>
    </div>
  );
};

/**
 * Model agreement indicator
 */
const ModelAgreementIndicator = ({ modelScores }) => {
  if (!modelScores) return null;

  const scores = [
    { name: 'IF', value: modelScores.isolationForest || modelScores.isolation_forest },
    { name: 'AE', value: modelScores.autoencoder },
    { name: 'Stat', value: modelScores.statistical },
  ].filter((s) => s.value !== undefined && s.value !== null);

  if (scores.length === 0) return null;

  // Calculate agreement (low std = high agreement)
  const values = scores.map((s) => s.value);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
  const std = Math.sqrt(variance);
  const agreement = Math.max(0, 1 - std * 2);
  const agreementLabel =
    agreement > 0.8 ? 'High' : agreement > 0.5 ? 'Medium' : 'Low';

  return (
    <div className="text-xs">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-gray-500">Model Agreement:</span>
        <span
          className={`font-medium ${
            agreement > 0.8
              ? 'text-green-600'
              : agreement > 0.5
              ? 'text-yellow-600'
              : 'text-red-600'
          }`}
        >
          {agreementLabel}
        </span>
      </div>
      <div className="flex gap-3">
        {scores.map((score) => (
          <div key={score.name} className="flex items-center gap-1">
            <span className="text-gray-400">{score.name}:</span>
            <span className="font-mono">{(score.value * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Anomaly type badge
 */
const AnomalyTypeBadge = ({ anomalyType }) => {
  const config = ANOMALY_TYPE_CONFIG[anomalyType];
  if (!config) return null;

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
    >
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </div>
  );
};

/**
 * AnomalyAlertBanner - Enhanced alert banner for anomaly detection results
 */
const AnomalyAlertBanner = ({ anomaly, onDismiss }) => {
  if (!anomaly || anomaly.alertLevel === 'none') {
    return null;
  }

  const alertConfig = ALERT_LEVEL_CONFIG[anomaly.alertLevel] || ALERT_LEVEL_CONFIG.low;
  const anomalyTypeConfig = ANOMALY_TYPE_CONFIG[anomaly.anomalyType];

  return (
    <div
      className={`mb-4 p-4 rounded-lg border ${alertConfig.borderColor} ${
        alertConfig.solid ? alertConfig.bgColor : alertConfig.bgColor
      }`}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`font-semibold text-lg ${
              alertConfig.solid ? 'text-white' : alertConfig.textColor
            }`}
          >
            Anomaly Detected
          </span>
          {anomaly.anomalyType && <AnomalyTypeBadge anomalyType={anomaly.anomalyType} />}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1 rounded text-sm font-semibold uppercase ${
              alertConfig.solid
                ? 'bg-white text-red-600'
                : `${alertConfig.bgColor} ${alertConfig.textColor}`
            }`}
          >
            {alertConfig.label}
          </span>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className={`p-1 rounded hover:bg-black/10 ${
                alertConfig.solid ? 'text-white' : 'text-gray-500'
              }`}
            >
              x
            </button>
          )}
        </div>
      </div>

      {/* Score and Description Row */}
      <div className={`mb-3 ${alertConfig.solid ? 'text-white/90' : 'text-gray-700'}`}>
        <div className="flex items-center gap-4 text-sm">
          <span>
            Score: <strong>{(anomaly.anomalyScore * 100).toFixed(0)}%</strong>
          </span>
          {anomaly.expectedLocation && (
            <span>
              Expected: <strong>{anomaly.expectedLocation}</strong>
            </span>
          )}
          {anomaly.currentLocationType && (
            <span>
              Current: <strong>{anomaly.currentLocationType}</strong>
            </span>
          )}
        </div>
        {anomalyTypeConfig && (
          <p className={`text-sm mt-1 ${alertConfig.solid ? 'text-white/80' : 'text-gray-500'}`}>
            {anomalyTypeConfig.description}
          </p>
        )}
      </div>

      {/* Deviation Details */}
      {anomaly.deviationDetails && (
        <div
          className={`mb-3 text-xs grid grid-cols-2 gap-2 ${
            alertConfig.solid ? 'text-white/80' : 'text-gray-600'
          }`}
        >
          {anomaly.deviationDetails.timeDeviationMinutes !== null &&
            anomaly.deviationDetails.timeDeviationMinutes !== undefined && (
              <div>
                Time Deviation:{' '}
                <span className="font-medium">
                  {Math.abs(anomaly.deviationDetails.timeDeviationMinutes)} min
                  {anomaly.deviationDetails.timeDeviationMinutes > 0 ? ' late' : ' early'}
                </span>
              </div>
            )}
          {anomaly.deviationDetails.distanceFromExpectedKm !== null &&
            anomaly.deviationDetails.distanceFromExpectedKm !== undefined && (
              <div>
                Distance from Expected:{' '}
                <span className="font-medium">
                  {anomaly.deviationDetails.distanceFromExpectedKm.toFixed(1)} km
                </span>
              </div>
            )}
          {anomaly.deviationDetails.velocityMph !== null &&
            anomaly.deviationDetails.velocityMph !== undefined && (
              <div>
                Velocity:{' '}
                <span className="font-medium">
                  {anomaly.deviationDetails.velocityMph.toFixed(0)} mph
                </span>
              </div>
            )}
          {anomaly.deviationDetails.headingChangeDegrees !== null &&
            anomaly.deviationDetails.headingChangeDegrees !== undefined && (
              <div>
                Heading Change:{' '}
                <span className="font-medium">
                  {anomaly.deviationDetails.headingChangeDegrees.toFixed(0)}deg
                </span>
              </div>
            )}
        </div>
      )}

      {/* Confidence and Model Scores */}
      <div
        className={`pt-2 border-t ${
          alertConfig.solid ? 'border-white/20' : 'border-gray-200'
        }`}
      >
        <div
          className={`flex flex-wrap items-center justify-between gap-4 ${
            alertConfig.solid ? 'text-white/90' : ''
          }`}
        >
          <ConfidenceMeter confidence={anomaly.confidence} />
          <ModelAgreementIndicator modelScores={anomaly.modelScores} />
        </div>
      </div>
    </div>
  );
};

export default AnomalyAlertBanner;
export { AnomalyTypeBadge, ConfidenceMeter, ModelAgreementIndicator, ALERT_LEVEL_CONFIG, ANOMALY_TYPE_CONFIG };
